// Single configured ky instance per docs/ui/frontend-conventions.md §6.4.
// Auth0 token interceptor + traceparent propagation + 401 → forceLogout are
// wired here per the operator-ui TDS §6.3 and auth0-frontend-tds.md §8.1.

import ky, { type KyInstance } from 'ky';
import { getAccessToken } from '@/auth/token-holder';
import {
  enterTenantMismatch,
  forceLogout,
  type ITenantMismatch,
  type TenantMismatchReason,
} from '@/auth/force-logout';
import { getSessionUrn } from '@/auth/session-urn';

// Dev-only multi-operator simulator. URL `?dev_user=<auth0-sub>` (e.g.
// `?dev_user=auth0|alice`) is captured once at boot into sessionStorage so
// every subsequent request in the tab carries `X-Dev-Operator-Override`,
// which the BFF JwtAuthGuard honors when AUTH0_DEV_BYPASS=true.
//
// Open two tabs with `?dev_user=auth0|alice` and `?dev_user=auth0|bob` to
// exercise real lock contention without standing up real Auth0 logins.
const DEV_USER_STORAGE_KEY = 'nexa.devOperatorOverride';

function captureDevUserOverride(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const devUser = params.get('dev_user');
    if (devUser) {
      window.sessionStorage.setItem(DEV_USER_STORAGE_KEY, devUser);
      params.delete('dev_user');
      const next = params.toString();
      const url = window.location.pathname + (next ? `?${next}` : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    }
  } catch {
    // sessionStorage unavailable (private mode, SSR) — silently fall through.
  }
}

function getDevUserOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(DEV_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

captureDevUserOverride();

export interface HttpError {
  status: number;
  code?: string;
  message: string;
  traceparent?: string;
}

/**
 * Not every 401 means "your session expired".
 *
 * `operator-bff:tenant-mismatch-401@v1` (docs/tenant-isolation-model.md §4.4) is a 401 that
 * re-authenticating cannot fix: the operator's Auth0 session is valid, it just belongs to a
 * different airline than this console serves. Sending it down the ordinary
 * `forceLogout → /login` path produces a redirect loop, because silent auth happily mints
 * another token for the same wrong airline.
 *
 * The wire contract, frozen:
 *
 * ```json
 * { "error": "tenant_mismatch", "reason": "org_pin" | "airline_claim",
 *   "expectedAirlineUrn": "urn:airline:latam",
 *   "tokenAirlineUrn": "urn:airline:avianca" | null,
 *   "message": "…" }
 * ```
 *
 * Reads a CLONE — the `beforeError` hook below consumes the body too, and a Response body
 * can only be read once. Returns null for any other 401 shape, including an unparseable one,
 * so a proxy-mangled body degrades to the ordinary session-expired path rather than
 * stranding the operator on a terminal screen they did not earn.
 */
async function readTenantMismatch(response: Response): Promise<ITenantMismatch | null> {
  try {
    const body = (await response.clone().json()) as Record<string, unknown>;
    if (body?.error !== 'tenant_mismatch') return null;
    const reason: TenantMismatchReason =
      body.reason === 'org_pin' ? 'org_pin' : 'airline_claim';
    return {
      reason,
      expectedAirlineUrn:
        typeof body.expectedAirlineUrn === 'string' ? body.expectedAirlineUrn : null,
      // Null by contract when the org pin rejected the token before decoding its claims.
      tokenAirlineUrn: typeof body.tokenAirlineUrn === 'string' ? body.tokenAirlineUrn : null,
      message: typeof body.message === 'string' ? body.message : null,
    };
  } catch {
    return null;
  }
}

export const http: KyInstance = ky.create({
  prefixUrl: import.meta.env.VITE_API_BASE_URL,
  timeout: 10_000,
  retry: {
    limit: 2,
    methods: ['get', 'put', 'delete'],
    // 401 is deliberately absent: neither an expired session nor a tenant mismatch is
    // fixed by repeating the request, and retrying a tenant mismatch would hammer the BFF
    // with a token it has already refused.
    statusCodes: [408, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        try {
          const token = await getAccessToken();
          request.headers.set('Authorization', `Bearer ${token}`);
        } catch {
          // Token getter not initialised yet (AuthBridge not mounted). Allow
          // the request through unauthenticated; the BFF will 401 and the
          // afterResponse hook below will trigger forceLogout.
        }
        const devUser = getDevUserOverride();
        if (devUser) {
          request.headers.set('X-Dev-Operator-Override', devUser);
        }
        // Per-tab session URN (TDS §4). The BFF keys the lock index on this so
        // WS-disconnect release frees only this tab's locks — never a sibling
        // tab held by the same operator.
        request.headers.set('X-Session-Urn', getSessionUrn());
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status !== 401) return response;

        // Terminal: the operator is at the wrong airline's console. Latch + tear down
        // WITHOUT redirecting; TenantGuard renders the explanation. Idempotent, so the
        // other in-flight requests failing the same way cost nothing.
        const mismatch = await readTenantMismatch(response);
        if (mismatch) {
          await enterTenantMismatch(mismatch);
          return response;
        }

        // Ordinary expired/invalid session — unchanged behaviour.
        await forceLogout({ reason: 'token-rejected' });
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        if (response && response.body) {
          try {
            const body = (await response.json()) as Partial<HttpError>;
            error.message = body.message ?? error.message;
            // The body is consumed here, so keep what callers switch on: the BFF's `code`
            // (`{ code, message, … }` — e.g. ESCALATION, VERSION_CONFLICT) and the whole body.
            Object.assign(error, {
              code: typeof body?.code === 'string' ? body.code : undefined,
              body,
            });
          } catch {
            // fall through with the default ky error message
          }
        }
        return error;
      },
    ],
  },
});
